import i18next from 'i18next'
import { Alerts, CurrentAlert, CurrentAlertType } from 'stylewhere/api'
import { SystemError } from 'stylewhere/shared/errors'
import Storage from './Storage'

const ALERT_CLIENT = 'station'
const ALERT_POLLING_INTERVAL = 30000
const MAX_DISMISSED_ALERTS = 200
const SEVERITY_ORDER: Record<string, number> = { ERROR: 0, WARN: 1, INFO: 2, OK: 3 }

const TRANSLATION_KEYS: Record<string, string> = {
  zh_cn: 'zh_CN',
  br: 'pt_BR'
}

export interface DisplayedAlert {
  id: string
  version: string
  type: CurrentAlertType
  title: string
  body?: string
}

const getVersion = (alert: CurrentAlert) => `${alert.id}|${alert.lastModifyDate}`

const getAlertText = (alert: CurrentAlert, language: string) => {
  const key = TRANSLATION_KEYS[language] ?? language
  const translation = alert.translations?.[key]
  return { title: translation?.title || alert.title, body: translation?.body || alert.body || undefined }
}

class CurrentAlerts {
  private userId?: string
  private timer?: ReturnType<typeof setInterval>
  private lastAlerts: CurrentAlert[] = []
  private readonly dismissedInSession = new Set<string>()
  private displayed: DisplayedAlert[] = []
  private readonly listeners = new Set<() => void>()
  private requestId = 0
  private endpointMissing = false

  constructor() {
    document.addEventListener('visibilitychange', this.syncTimer)
    i18next.on('languageChanged', this.publish)
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot = () => this.displayed

  start(userId: string) {
    if (this.userId === userId) return
    if (this.userId) this.stop()
    this.userId = userId
    this.syncTimer()
  }

  stop() {
    this.requestId++
    this.userId = undefined
    this.endpointMissing = false
    this.syncTimer()
    this.lastAlerts = []
    this.dismissedInSession.clear()
    this.publish()
  }

  dismiss(alert: DisplayedAlert, dontShowAgain: boolean) {
    this.dismissedInSession.add(alert.version)
    this.publish()
    if (dontShowAgain && this.userId) this.saveDismissed(alert.version)
  }

  private getStorageKey() {
    return `dismissedAlerts:${this.userId}`
  }

  private loadDismissed(): string[] {
    const dismissed = Storage.load(this.getStorageKey(), [])
    return Array.isArray(dismissed) ? dismissed : []
  }

  private saveDismissed(version: string) {
    Storage.save(
      this.getStorageKey(),
      [...this.loadDismissed().filter((v) => v !== version), version].slice(-MAX_DISMISSED_ALERTS)
    )
  }

  private isSuperseded(requestId: number) {
    return !this.userId || requestId !== this.requestId
  }

  private readonly syncTimer = () => {
    const shouldRun = !!this.userId && !this.endpointMissing && document.visibilityState === 'visible'
    if (shouldRun && !this.timer) {
      void this.refresh()
      this.timer = setInterval(() => void this.refresh(), ALERT_POLLING_INTERVAL)
    } else if (!shouldRun && this.timer) {
      clearInterval(this.timer)
      this.timer = undefined
    }
  }

  private async refresh() {
    const requestId = ++this.requestId
    let alerts: CurrentAlert[]
    try {
      alerts = await Alerts.current(ALERT_CLIENT)
    } catch (error) {
      if (error instanceof SystemError && error.errorCode === 404 && !this.isSuperseded(requestId)) {
        console.warn('Current alerts endpoint not available, polling stopped')
        this.endpointMissing = true
        this.syncTimer()
      }
      return
    }
    if (this.isSuperseded(requestId)) return
    this.lastAlerts = Array.isArray(alerts) ? alerts.filter((alert) => alert?.id) : []
    this.publish()
  }

  private readonly publish = () => {
    const dismissedForever = new Set<string>(this.userId ? this.loadDismissed() : [])
    const language = i18next.resolvedLanguage ?? i18next.language ?? 'en'
    const seen = new Set<string>()
    this.displayed = this.lastAlerts
      .filter((alert) => {
        const version = getVersion(alert)
        if (seen.has(alert.id) || this.dismissedInSession.has(version) || dismissedForever.has(version)) return false
        seen.add(alert.id)
        return true
      })
      .map((alert) => ({
        id: alert.id,
        version: getVersion(alert),
        type: alert.type,
        ...getAlertText(alert, language)
      }))
      .toSorted((a, b) => (SEVERITY_ORDER[a.type] ?? 99) - (SEVERITY_ORDER[b.type] ?? 99))
    this.listeners.forEach((listener) => listener())
  }
}

export default new CurrentAlerts()
